<template>
    <div
        v-if="visible"
        class="fixed w-screen h-screen left-0 top-0 bg-black/60 z-20 flex items-center justify-center cursor-pointer"
        @keydown.esc="hide"
        @click.self.exact="hide"
    >
        <div class="w-1/3 min-w-96 bg-base-200 flex flex-col justify-between p-4 cursor-auto gap-4">
            <div>Instillinger for KID</div>
            <div>{{ account?.name }} - {{ account?.bban }} - {{ account?.bookkeepingAccountNo }}</div>
            <SelectButton
                v-if="state !== 'edit'"
                v-model="state"
                :options="stateOptions"
                option-label="optionLabel"
                option-value="optionValue"
            />
            <general-input v-model="customerNoLength" type="number" label="Kundenummer lengde"/>
            <general-input v-model="invoiceNoLength" type="number" label="Fakturanummer lengde"/>
            <general-input
                v-model="kidLength"
                disabled
                type="number"
                label="Kid lengde"
            />
            <general-input
                v-if="state === 'onboard'"
                v-model="contactName"
                type="string"
                label="Kontaktnavn"
            />
            <general-input
                v-if="state === 'onboard'"
                v-model="contactEmail"
                type="string"
                label="Kontaktepost"
            />
            <general-input
                v-if="state !== 'onboard'"
                v-model="agreementId"
                type="string"
                label="Avtale id"
            />
            <Select v-model="modulus" :options="['MOD_11', 'MOD_10']"/>
            <div v-for="error in validationErrors" :key="error" class="text-error">{{ error }}</div>
            <div v-for="error in errorResponses" :key="error" class="text-error">{{ error }}</div>
            <div class="flex" :class="state === 'edit' ? 'justify-between' : 'justify-end'">
                <UButton v-if="state === 'edit'" color="error" @click="onRemove">
                    <div class="i-ph-trash-fill"/>
                    Fjern avtale
                </UButton>
                <div class="flex gap-2">
                    <UButton
                        size="md"
                        :color="!valid ? 'neutral' : 'secondary'"
                        :variant="!valid ? 'subtle' : 'solid'"
                        :disabled="!valid"
                        @click="onSave"
                    >
                        Lagre
                    </UButton>
                    <UButton size="md" @click="onCancel">Avbryt</UButton>
                </div>
            </div>
            <div v-if="state !== 'onboard'" class="text-error">Obs! Pass på at KID lengde, modulus og avtale id er riktig i henhold til avtalen med banken din.</div>
            <div v-if="state === 'edit'" class="text-sm">
                Fjern avtale fjerner bare avtalen i Conta Pro, avtalen blir ikke avsluttet i banken.
                Dette må gjøres ved å ta kontakt med din bank og be dem avslutte KID avtalen.
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

// TODO why do we not generalize popups to a HOC?
import {computed, onMounted, ref} from 'vue'
import {RichBankAccount} from '@/bank/bank.types'
import GeneralInput from '@/components/GeneralInput.vue'
import SelectButton from 'primevue/selectbutton'
import {organization} from '@/organization/organization.service'
import {onboardOcr, registerOcr, removeOcr, updateOcr} from '@/bank/account/account.service'
import Select from 'primevue/select'
import {OcrAgreement} from '@/bank/account/account.types'


type StateType = 'register' | 'onboard' | 'edit'

const state = ref<StateType>('onboard')
const stateOptions = [
    {
        optionLabel: 'Registrer',
        optionValue: 'register'
    },
    {
        optionLabel: 'Bestill ny',
        optionValue: 'onboard'
    }
]

const visible = ref(false)
const emit = defineEmits(['save', 'close'])
const account = ref<RichBankAccount>()
const customerNoLength = ref(10)
const invoiceNoLength = ref(10)
const contactName = ref<string>()
const contactEmail = ref<string>()
const agreementId = ref<string>()
const modulus = ref<'MOD_10' | 'MOD_11'>('MOD_11')
const kidLength = computed(() => customerNoLength.value + invoiceNoLength.value + 1)

const clear = () => {
    state.value = 'onboard'
    account.value = undefined
    customerNoLength.value = 10
    invoiceNoLength.value = 10
    contactName.value = undefined
    contactEmail.value = undefined
    agreementId.value = undefined
    modulus.value = 'MOD_11'
}

const valid = computed(() => validationErrors.value.length === 0)

const validationErrors = computed(() => {
    const e = []

    if (kidLength.value <= 3 || kidLength.value > 25) {
        e.push(`Lengde på KID må være mellom 3 og 25, ${kidLength.value} er ikke gyldig.`)
    }

    if (!account.value || !account.value.bankUuid) {
        // TODO bedre feilmelding
        e.push('Bankintegrasjon må være aktivert, med samtykke og relevant bankkonto.')
    }

    if (state.value === 'onboard') {
        if (!contactName.value || contactName.value.length === 0) {
            e.push('Navn mangler')
        }

        if (!contactEmail.value || contactEmail.value?.length === 0) {
            e.push('E-post mangler')
        }
    }

    if (state.value !== 'onboard') {
        if (!agreementId.value || agreementId.value.length === 0) {
            e.push('Avtale id mangler')
        }
    }

    return e
})

const errorResponses = ref<string[]>([])

const show = (bankAccount: RichBankAccount, existingAgreement?: OcrAgreement) => {
    visible.value = true
    account.value = bankAccount
    if (existingAgreement) {
        agreementId.value = existingAgreement.agreementId
        customerNoLength.value = existingAgreement.customerNoLength
        invoiceNoLength.value = existingAgreement.invoiceNoLength
        modulus.value = existingAgreement.kidModulus === 11 ? 'MOD_11' : 'MOD_10'
        state.value = 'edit'
    }
}

const onSave = async () => {
    // What an ugly switch case
    switch (state.value) {
        case 'register':
            await onRegister()
            break
        case 'onboard':
            await onOnboard()
            break
        case 'edit':
            await onUpdate()
            break
        default:
            // allows for exhaustive check of switch case apparently
            // eslint-disable-next-line
            const _: never = state.value
            throw new Error(`Unhandled state case: ${state.value}`)
    }
}

const onOnboard = async () => {
    errorResponses.value = []
    if (account.value?.bankUuid && contactName.value && contactEmail.value) {
        try {
            await onboardOcr({
                accountUuid: account.value.bankUuid,
                contactPersonName: contactName.value,
                contactPersonEmail: contactEmail.value,
                customerNoLength: customerNoLength.value,
                invoiceNoLength: invoiceNoLength.value,
                kidModulus: modulus.value
            })
            visible.value = false
            emit('save')
            clear()
        } catch (e: any) {
            if (e.response && e.response.data && e.response.data.info) {
                errorResponses.value = Object.values(e.response.data.info)
            }
        }
    }
}

const onUpdate = async () => {
    if (valid.value && account.value?.bankUuid && agreementId.value) {
        await updateOcr({
            accountUuid: account.value.bankUuid,
            agreementId: agreementId.value,
            customerNoLength: customerNoLength.value,
            invoiceNoLength: invoiceNoLength.value,
            kidModulus: modulus.value
        })
        visible.value = false
        emit('save')
        clear()
    }
}


const onRegister = async () => {
    if (valid.value && account.value?.bankUuid && agreementId.value) {
        await registerOcr({
            accountUuid: account.value.bankUuid,
            agreementId: agreementId.value,
            customerNoLength: customerNoLength.value,
            invoiceNoLength: invoiceNoLength.value,
            kidModulus: modulus.value
        })
        visible.value = false
        emit('save')
        clear()
    }
}

const onRemove = async () => {
    if (state.value === 'edit' && account.value?.bankUuid) {
        await removeOcr({accountUuid: account.value.bankUuid})
        visible.value = false
        emit('save')
    }
}

const onCancel = () => {
    visible.value = false
    emit('close')
    clear()
}

const hide = () => {
    visible.value = false
    emit('close')
    clear()
}

onMounted(async () => {
    contactName.value = organization.value?.contactInfo?.name
    contactEmail.value = organization.value?.contactInfo?.email
})

defineExpose({
    show,
    hide
})

</script>

<style scoped>

</style>